/**
 * All Sites ▸ Alert Board — the cross-site honeycomb, next to the Live Wall in the same scope.
 *
 * Every camera of every site on one canvas, a panel per site, one merged alert feed and one
 * docked alert panel. It is the same shared board the per-site page mounts; what differs is
 * only the api it is given — `createMultiSiteHexAlertsApi` fans the per-node tunnel calls out
 * across the estate — and `singleSite` being off, which is what makes the honeycomb draw its
 * site panels and offer the Site filter ("All sites" plus a chip per site).
 *
 * WHY THE PAGE, NOT AlertBoardPage: the per-site wrapper is bound to the selected node at
 * every turn — its api reads the stored scope, its modal fetches the alert from the selected
 * node, its toggle navigates to that node's map. In All Sites scope there IS no selected node,
 * so those all have to be answered per ALERT instead: the board's camera ids are
 * site-qualified (`nodeId::cameraId`), and this page reads the site back out of them.
 *
 * LAYOUT: the board's root is `flex h-full flex-col` with a `lg:flex-1` canvas inside it, so it
 * needs a parent with a DEFINITE height — Layout's <main> provides one, and `h-full` here
 * passes it down. A plain auto-height wrapper collapses the canvas to zero and the honeycomb
 * vanishes.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  AlertBoardPage as SharedAlertBoardPage,
  Modal,
  RecordPager,
  useApi,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '@trinetra/shared';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import HexLivePlayer, { HexStreamProvider } from '../components/hexagon/HexLivePlayer';
import {
  createMultiSiteHexAlertsApi,
  splitSiteCameraKey,
  type SiteRef,
} from '../api/multiSiteHexAlerts.adapter';
import { AlertDetailView } from './AlertDetailPage';

export default function AllSitesAlertBoardPage() {
  const { nodes, selectNode } = useNode();
  const { mapLayouts } = useApi();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  // WHICH sites the board covers, as a value that only changes when the roster does. The node
  // list is re-polled every 10s (status, metrics, session counts), and the board tears down and
  // rebuilds its entire subscription — N sockets, N snapshots — whenever its api changes, so
  // anything derived from `nodes` has to be keyed on the ids alone.
  const siteKey = useMemo(() => nodes.map((n) => n.nodeId).sort().join(','), [nodes]);
  const sites = useMemo<SiteRef[]>(
    () => nodes.map((n) => ({ nodeId: n.nodeId, name: n.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteKey],
  );

  // Offline sites are deliberately INCLUDED: their cameras simply fail to load and their
  // events socket retries with backoff, refetching that site's alerts the moment its tunnel
  // comes back. Filtering by status instead would rebuild every other site's subscription each
  // time any one site flapped.
  const alertsApi = useMemo(() => createMultiSiteHexAlertsApi(sites), [sites]);

  // The alert being read, held as the panel's LIST plus a cursor rather than as one alert: an
  // operator working a board sweeps its alerts, and pages with the arrow keys instead of
  // closing and re-clicking.
  const [viewing, setViewing] = useState<{ list: ViewAlertTarget[]; index: number } | null>(null);
  const current = viewing ? viewing.list[viewing.index] : null;
  // Every target carries a site-qualified camera id, which is how a cross-site board knows
  // whose tunnel to fetch this record through.
  const currentSite = current ? splitSiteCameraKey(current.cameraId).nodeId : null;

  /** Leave the board for a page that only exists inside one site, taking the scope with us —
   *  otherwise the destination opens in All Sites and finds no node to ask. */
  const openInSite = useCallback(
    (nodeId: string | null, path: string) => {
      if (nodeId) selectNode(nodeId);
      navigate(path);
    },
    [navigate, selectNode],
  );

  return (
    <HexStreamProvider>
      <SharedAlertBoardPage
        alertsApi={alertsApi}
        mapApi={mapLayouts}
        PlayerComponent={HexLivePlayer}
        className="p-3 sm:p-6"
        // Pinned to the honeycomb, no toggle: a floor plan belongs to ONE site, so the map
        // layout has nothing to show across an estate.
        view="hex"
        allowedViews={['hex']}
        onViewChange={() => {}}
        // A panel per site, and the Site filter row that goes with it. This is the board the
        // shared page's multi-site path was written for.
        singleSite={false}
        // No node is selected in this scope and none is required — the board reloads when the
        // ROSTER changes instead.
        nodeKey={siteKey}
        onOpenRecordings={
          hasPermission('recordings.view')
            ? (cameraKey: string) => {
                const { nodeId, cameraId } = splitSiteCameraKey(cameraKey);
                openInSite(nodeId, `/recordings?camera=${cameraId}`);
              }
            : undefined
        }
        onViewAlert={({ id, cameraId, siblings, index }: ViewAlertRequest) => {
          // No alert id means the card carries no stored row yet (the node broadcasts before
          // its storage lane writes) — there is nothing to open, so fall back to that site's
          // alert list.
          if (!id) {
            const { nodeId, cameraId: camId } = splitSiteCameraKey(cameraId);
            openInSite(nodeId, `/alerts?camera_id=${camId}`);
            return;
          }
          // Keep the panel's list so the arrow keys can walk it. Alerts without a stored row
          // are dropped: stepping onto one would open a record that cannot be fetched.
          const list = (siblings || []).filter((s) => !!s.id);
          const at = list.findIndex((s) => s.id === id);
          setViewing(
            list.length
              ? { list, index: at >= 0 ? at : (index ?? 0) }
              : { list: [{ id, cameraId, name: cameraId }], index: 0 },
          );
        }}
      />

      <Modal
        open={!!current}
        onClose={() => setViewing(null)}
        title={current && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{current.alertName || 'Alert'}</span>
            <span className="text-[13px] font-normal text-muted-foreground">· Alert Details</span>
          </span>
        )}
        ariaLabel="Alert details"
        subtitle={current?.name}
        size="full"
        headerActions={current && viewing && (
          <>
            {/* Steps through the alerts the panel was showing when this one was opened —
                buttons and the arrow keys both. */}
            <RecordPager
              index={viewing.index}
              count={viewing.list.length}
              onIndexChange={(i) => setViewing({ list: viewing.list, index: i })}
              noun="alerts"
            />
            <button
              type="button"
              // The routed alert page reads the SELECTED site, so opening it from here has to
              // switch scope to the alert's own site first.
              onClick={() => openInSite(currentSite, `/alerts/${current.id}`)}
              title="Open the full alert page"
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
            </button>
          </>
        )}
      >
        {/* Keyed on the alert id so stepping to another card refetches instead of showing the
            previous alert's data while the new one loads. `nodeId` is the alert's own site —
            there is no selected one to fall back on here. */}
        {current?.id && (
          <AlertDetailView
            key={current.id}
            alertId={current.id}
            nodeId={currentSite || undefined}
            showTitle={false}
          />
        )}
      </Modal>
    </HexStreamProvider>
  );
}
